import Dinero from 'dinero.js';

import { type SubscriptionBillingCycle } from '@air/api/types';

import { PLAN_CYCLE_UNIT } from '~/constants/planCycle';
import { CENTS_PER_DOLLAR } from '~/utils/CheckoutUtils';

export type FormatPriceOptions = {
  /**
   * Cents-rendering policy:
   * - `'auto'` (default): show cents only when the amount has a non-zero cent remainder
   * - `'always'`: always render `$X,XXX.YY`
   * - `'never'`: always render `$X,XXX` (round to whole dollars at display)
   */
  showCents?: 'auto' | 'always' | 'never';
  /**
   * Wrap negative amounts in parens (`($5.00)`) instead of using a leading minus (`-$5.00`).
   * Accounting-style convention; used for credit/refund line items.
   */
  parensForNegative?: boolean;
};

/**
 * Canonical currency formatter for cent-denominated amounts. By default shows cents only
 * when the amount has a non-zero cent remainder (e.g. `$25` for 2500, `$25.50` for 2550).
 * Pass `showCents: 'always'` for invoice-style rows or `'never'` for compact displays.
 */
export const formatPrice = (cents: number, options: FormatPriceOptions = {}): string => {
  const { showCents = 'auto', parensForNegative = false } = options;
  const absCents = Math.abs(Math.round(cents));
  const includesCents = showCents === 'always' || (showCents === 'auto' && absCents % CENTS_PER_DOLLAR !== 0);
  const formatted = Dinero({ amount: absCents }).toFormat(includesCents ? '$0,0.00' : '$0,0');

  if (cents < 0) {
    return parensForNegative ? `(${formatted})` : `-${formatted}`;
  }

  return formatted;
};

/**
 * Formats a price with a per-cycle suffix (e.g. `$25/month`, `$420/year`).
 * Inherits `formatPrice`'s cents-rendering policy; defaults to `'auto'`.
 */
export const formatPricePerCycle = (
  priceInCents: number,
  billingCycle: SubscriptionBillingCycle,
  options?: Pick<FormatPriceOptions, 'showCents'>,
): string => {
  return `${formatPrice(priceInCents, options)}/${PLAN_CYCLE_UNIT[billingCycle]}`;
};

/**
 * Formats a per-cycle price delta (e.g. `+ $4.20/month`, `- $420/year`).
 * Returns `null` when the delta is zero so the caller can omit the row entirely.
 * Inherits `formatPrice`'s cents-rendering policy; defaults to `'auto'`.
 */
export const formatPricePerCycleDelta = (
  deltaCents: number,
  billingCycle: SubscriptionBillingCycle,
  options?: Pick<FormatPriceOptions, 'showCents'>,
): string | null => {
  if (deltaCents === 0) {
    return null;
  }
  const sign = deltaCents > 0 ? '+' : '-';

  return `${sign} ${formatPricePerCycle(Math.abs(deltaCents), billingCycle, options)}`;
};
